import { useCallback } from "react";
import { AnswerCheckerHookReturn, ExerciseData } from "../types";
import { getMistakePenalty, levenshteinDistance, normalizeAnswer } from "../utils";

export const useAnswerChecker = (
  currentLevel: number,
  consecutiveMistakes: number
): AnswerCheckerHookReturn => {
  const checkAnswer = useCallback(
    async (userAnswer: string, exerciseData: ExerciseData) => {
      if (!userAnswer.trim()) {
        return {
          isCorrect: false,
          feedback: "Please enter an answer.",
          shouldAdvanceLevel: false,
          levelChange: 0,
        };
      }

      const normalizedUserAnswer = normalizeAnswer(userAnswer);
      const normalizedCorrectAnswer = normalizeAnswer(exerciseData.correctAnswer);

      // Check if answer is correct
      const isAnswerCorrect = normalizedUserAnswer === normalizedCorrectAnswer;

      // Check for near-miss (Levenshtein distance)
      const distance = levenshteinDistance(normalizedUserAnswer, normalizedCorrectAnswer);
      const isNearMiss = distance <= 2 && normalizedUserAnswer.length > 2;

      if (isAnswerCorrect) {
        return {
          isCorrect: true,
          feedback: `✨ Correct! "${exerciseData.correctAnswer}" - Level ${currentLevel}/100`,
          shouldAdvanceLevel: true,
          levelChange: 1,
        };
      } else if (isNearMiss) {
        return {
          isCorrect: false,
          feedback: `Almost! The word is "${exerciseData.correctAnswer}". Try again!`,
          shouldAdvanceLevel: false,
          levelChange: 0,
        };
      } else {
        const penalty = getMistakePenalty(consecutiveMistakes);
        return {
          isCorrect: false,
          feedback: `The word was "${exerciseData.correctAnswer}". Moving down ${penalty} level${penalty > 1 ? "s" : ""}`,
          shouldAdvanceLevel: false,
          levelChange: -penalty,
        };
      }
    },
    [currentLevel, consecutiveMistakes]
  );

  return {
    checkAnswer,
    normalizeAnswer,
  };
};
